import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import Alert from "../components/Alert";
import InterestSubscription from "../components/InterestSubscription";
import Loader from "../components/Loader";
import SearchBox from "../components/SearchBox";
import SuggestionModal from "../components/SuggestionModal";
import { removeSubscription } from "../redux/authSlice";
import {
  clearAlertMessage,
  fetchDeepParent,
  fetchNodes,
  newSuggestion,
} from "../redux/nodesSlice";
// import { getNotificationCounter } from "../redux/notificationCounter";
import "../style/Selector.css";
import Utils from "../utils/Utils";

const { getNodeByNumberedPath, buildBreadcrumbFromNumberedPath } = Utils;
const fetchedParentIDs = [];

function Directory() {
  const { t } = useTranslation();
  const {
    status,
    nodes,
    nodesIndex,
    alertMessage,
    isAlertMessage,
    alertMessageType,
  } = useSelector((state) => state.nodes);
  const { isLoggedIn, profile } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const theme = useTheme();
  const pathToFetch = useRef("");
  const [showModal, setShowModal] = useState(false);
  const [isInterestSub, setIsInterestSub] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [result, setResult] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [paths, setPaths] = useState([]);
  const [viewLink, setViewLink] = useState("/");

  const [currentNumberedPath, setCurrentNumberedPath] = useState("");

  // fetch nodes when first mounts
  useEffect(() => {
    if (!nodes.name) {
      // Fetch root node using "root" as per API docs
      dispatch(fetchNodes({ path: "root" }));
      return;
    }
  }, [dispatch, nodes.name]);

  useEffect(() => {
    if (
      nodes.name &&
      !currentNumberedPath &&
      nodes.numberedPath !== undefined
    ) {
      setCurrentNumberedPath(nodes.numberedPath || "");
    }
  }, [nodes, currentNumberedPath]);

  // Get current node from index or fallback to tree traversal
  const current = useCallback(() => {
    if (nodesIndex && Object.keys(nodesIndex).length > 0) {
      const node = nodesIndex[currentNumberedPath];
      if (node) return node;

      // If currentNumberedPath is empty or undefined, return root
      if (!currentNumberedPath || currentNumberedPath === "") {
        return nodesIndex[""] || nodes;
      }

      // Node not in index, try tree traversal
      const foundNode = getNodeByNumberedPath(nodes, currentNumberedPath);
      if (foundNode) return foundNode;

      // Last resort: return root but this means something is wrong
      return nodes;
    }

    // Fallback to legacy path-based lookup if needed
    if (currentNumberedPath === "" || currentNumberedPath === undefined) {
      return nodes;
    }
    return getNodeByNumberedPath(nodes, currentNumberedPath) || nodes;
  }, [nodesIndex, currentNumberedPath, nodes])();

  // fetch more nodes when current has no children loaded yet
  useEffect(() => {
    // Skip if no current node or missing required fields
    if (!current || !current.name || !current._id) return;

    // Determine if we need to fetch children:
    // - children === undefined: never fetched, need to fetch
    // - children === []: could be truly empty OR cut off by maxDepth in initial fetch
    //   We'll check if this node has already been individually fetched

    const alreadyFetched = fetchedParentIDs.includes(current._id);

    // Skip if this node was already individually fetched (meaning children: [] is real)
    if (alreadyFetched && current.children !== undefined) return;

    // Skip if has children already loaded
    if (current.children && current.children.length > 0) return;

    // Fetch the node's children
    if (!alreadyFetched) {
      // Use numberedPath if available, otherwise fall back to path
      if (current.numberedPath !== undefined) {
        dispatch(fetchNodes({ numberedPath: current.numberedPath }));
        pathToFetch.current = current.numberedPath;
      } else if (current.path) {
        dispatch(fetchNodes({ path: current.path }));
        pathToFetch.current = current.path;
      }
      fetchedParentIDs.push(current._id);
    } else {
      // Check if the parent was fetched
      if (pathToFetch.current && nodesIndex[pathToFetch.current]) {
        const newParent = nodesIndex[pathToFetch.current];
        if (newParent?.children !== undefined) {
          setCurrentNumberedPath(newParent.numberedPath || "");
        }
      }
    }
  }, [current, dispatch, nodesIndex]);

  // update the parent when clicked on children
  const updateParent = useCallback((numberedPath) => {
    setCurrentNumberedPath(numberedPath);
  }, []);
  const deepPathToFetch = useRef("");

  // set parent by fetching if not available locally
  const setDeepParent = useCallback(
    async ({ pathToParent, numberedPath, isLocal, newParentID }) => {
      if (isLocal) {
        updateParent(numberedPath || pathToParent);
      } else {
        const targetPath = numberedPath || pathToParent;
        if (targetPath) {
          // Find the deepest available ancestor node in the tree
          // Split the path to walk through segments
          const pathSegments = targetPath.split(".");
          let lastAvailablePath = null;

          // Walk through the path segments to find the deepest available node
          for (let i = 1; i <= pathSegments.length; i++) {
            const testPath = pathSegments.slice(0, i).join(".");
            if (nodesIndex[testPath]) {
              lastAvailablePath = testPath;
            } else {
              // Stop when we hit a missing node
              break;
            }
          }

          // Dispatch with the starting path and ending path
          // Backend will fetch the full tree from start to end with all siblings
          dispatch(
            fetchDeepParent({
              numberedPath: lastAvailablePath || "root",
              endingPath: targetPath,
            })
          );
          deepPathToFetch.current = targetPath;
        }
      }
    },
    [updateParent, dispatch, nodesIndex]
  );

  // update current when deep parent fetched
  useEffect(() => {
    if (deepPathToFetch.current) {
      // Try to find the node in the index
      if (nodesIndex[deepPathToFetch.current]) {
        setCurrentNumberedPath(deepPathToFetch.current);
      } else {
        // Legacy fallback - try path-based lookup
        const node = getNodeByNumberedPath(nodes, deepPathToFetch.current);
        if (node && node.numberedPath !== undefined) {
          setCurrentNumberedPath(node.numberedPath);
        }
      }
      deepPathToFetch.current = "";
    }
  }, [nodes, nodesIndex]);

  // useEffect(() => {
  //   let intervalId;
  //   if (profile) {
  //     intervalId = setInterval(() => {
  //       if (profile) {
  //         dispatch(getNotificationCounter(profile._id));
  //       }
  //     }, 5000);
  //   } else {
  //     return () => {
  //       clearInterval(intervalId);
  //     };
  //   }
  // }, [dispatch, profile]);

  // useEffect(() => {
  //   if (profile) {
  //     dispatch(getNotificationCounter(profile._id));
  //   }
  // }, [profile, dispatch]);

  // breadcrumbs click - now uses numberedPath
  const removePathAndAdd = useCallback((targetNumberedPath) => {
    // Navigate to a specific numbered path when clicking breadcrumb
    setCurrentNumberedPath(targetNumberedPath || "");
  }, []);

  const pathRef = useRef(null);
  useEffect(() => {
    if (!pathRef.current) return;
    const container = pathRef.current;
    container.scrollLeft = container.scrollWidth - container.clientWidth;
  }, [current]);

  useEffect(() => {
    if (current) {
      // Build breadcrumb paths for display
      const breadcrumbs = buildBreadcrumbFromNumberedPath(
        nodesIndex,
        currentNumberedPath
      );
      setPaths(breadcrumbs); // Store full breadcrumb objects with name and numberedPath
      setIsExpanded(false);

      // Build view link using slug (last node name) for clean URLs
      // Generate slug from current node name for all nodes including root
      if (current.name) {
        const slug = current.name
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-]/g, "");
        setViewLink(`/${slug}`);
      } else {
        // Fallback if no name
        setViewLink("/");
      }
    }
  }, [current, currentNumberedPath, nodesIndex]);

  const handleShowModal = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setTextInput("");
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult("");
    const suggestion = {
      name: textInput,
      parent: current._id,
      suggested: true,
      searchable: true,
    };
    await dispatch(newSuggestion(suggestion));
    handleCloseModal();
  };

  const handleClick = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box sx={{ width: "100%", alignSelf: "center" }}>
      <Paper
        className="max-w-[600px] w-full mx-auto"
        elevation={3}
        sx={{
          mx: "auto",
          padding: "2rem 1rem",
          my: 2,
          borderRadius: 3,
        }}
      >
        <Typography variant="h4" component="h1" textAlign="center" my={2}>
          {t("selectYourInterest")}
        </Typography>
        {status === "success" ? (
          <>
            <SearchBox
              parent={current}
              updateParent={updateParent}
              setDeepParent={setDeepParent}
              t={t}
            />
            <Box>
              <Breadcrumbs
                className="path"
                ref={pathRef}
                maxItems={100}
                sx={{
                  flexWrap: "nowrap",
                  "& ol": {
                    flexWrap: "nowrap",
                    "& li": {
                      display: "flex",
                      flexWrap: "nowrap",
                      alignItems: "center",
                    },
                  },
                  mt: 1,
                }}
                aria-label="breadcrumb"
              >
                {paths && paths.length > 3 && !isExpanded
                  ? [
                      <Button
                        variant="text"
                        key={paths[0].numberedPath || paths[0].name}
                        color="inherit"
                        sx={{ textTransform: "none", minWidth: "unset" }}
                        onClick={() => removePathAndAdd(paths[0].numberedPath)}
                      >
                        <Typography
                          variant="span"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {paths[0].name}
                        </Typography>
                      </Button>,
                      paths.length !== 4 && [
                        <Button
                          variant="contained"
                          color="inherit"
                          key={"more"}
                          size="small"
                          sx={{
                            minWidth: "unset",
                            py: 0.05,
                            px: 0.05,
                            mx: 0.5,
                          }}
                          onClick={handleClick}
                        >
                          <Typography variant="span" sx={{ px: 0.75 }}>
                            &bull;&bull;&bull;
                          </Typography>
                        </Button>,
                      ],
                      paths
                        .slice(-3)
                        .slice(0, -1)
                        .map((breadcrumb) => [
                          <Button
                            variant="text"
                            color="inherit"
                            key={breadcrumb.numberedPath || breadcrumb.name}
                            sx={{
                              textTransform: "none",
                              minWidth: "unset",
                            }}
                            onClick={() =>
                              removePathAndAdd(breadcrumb.numberedPath)
                            }
                          >
                            <Typography
                              variant="span"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {breadcrumb.name}
                            </Typography>
                          </Button>,
                        ]),
                    ]
                  : paths.slice(0, -1).map((breadcrumb) => (
                      <Button
                        variant="text"
                        color="inherit"
                        key={breadcrumb.numberedPath || breadcrumb.name}
                        sx={{ textTransform: "none", minWidth: "unset" }}
                        onClick={() =>
                          removePathAndAdd(breadcrumb.numberedPath)
                        }
                      >
                        <Typography
                          variant="span"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {breadcrumb.name}
                        </Typography>
                      </Button>
                    ))}
              </Breadcrumbs>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignContent: "center",
                  mx: 1,
                  my: 1,
                }}
              >
                <Tooltip title={t("viewFeed")} placement="top">
                  <NavLink to={viewLink}>
                    <IconButton color="info">
                      <VisibilityIcon />
                    </IconButton>
                  </NavLink>
                </Tooltip>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: "#4caf50", mx: 1 }}
                >
                  {current.name}
                </Typography>
                {isLoggedIn && (
                  <Box component="span">
                    {profile?.subscribedInterests?.findIndex(
                      (i) => i.id === current._id
                    ) > -1 ? (
                      <Tooltip title={t("unsubscribe")} placement="top">
                        <IconButton
                          color="error"
                          onClick={() => {
                            dispatch(
                              // eslint-disable-next-line no-underscore-dangle
                              removeSubscription({ id: current._id })
                            );
                          }}
                          aria-label="unsubscribe"
                        >
                          <RemoveCircleIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("subscribe")} placement="top">
                        <IconButton
                          color="success"
                          onClick={() => setIsInterestSub(true)}
                          aria-label="subscribe"
                        >
                          <AddCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", m: 1 }}>
                <Button
                  variant="outlined"
                  sx={{ borderRadius: 10 }}
                  onClick={handleShowModal}
                  startIcon={<AddIcon />}
                >
                  {t("suggest")}
                  {current.name}
                </Button>
              </Box>
              <Box className="children">
                {/* eslint-disable-next-line no-nested-ternary */}
                {current.children === undefined ? (
                  <Loader />
                ) : current.children.length === 0 ? (
                  <Box textAlign="center" my={2}>
                    <span className="dash-bordered">{t("noMoreNode")}</span>
                  </Box>
                ) : (
                  [...current.children]
                    .sort((a, b) => {
                      // Sort alphabetically by name (case-insensitive)
                      const nameA = (a.name || "").toLowerCase();
                      const nameB = (b.name || "").toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((child) => (
                      <Button
                        color="inherit"
                        key={child.numberedPath || child._id}
                        variant="text"
                        sx={{
                          justifyContent: "flex-start",
                          color: theme.palette.text.primary,
                        }}
                        onClick={() =>
                          updateParent(child.numberedPath || child.path || "")
                        }
                      >
                        <Typography>{child.name}</Typography>
                      </Button>
                    ))
                )}
              </Box>
            </Box>
          </>
        ) : (
          <Loader />
        )}
        {showModal && (
          <SuggestionModal
            showModal={showModal}
            handleCloseModal={handleCloseModal}
            handleShowModal={handleShowModal}
            handleSubmit={handleSubmit}
            result={result}
            parent={current}
            setTextInput={setTextInput}
            t={t}
          />
        )}
        {isInterestSub && (
          <InterestSubscription
            profile={profile}
            interest={current}
            isInterestSub={isInterestSub}
            setIsInterestSub={setIsInterestSub}
            t={t}
          />
        )}
        <Alert
          isAlertMessage={isAlertMessage}
          alertMessage={alertMessage}
          alertMessageType={alertMessageType}
          onClose={() => dispatch(clearAlertMessage())}
        />
      </Paper>
    </Box>
  );
}

export default Directory;
